import { Badge } from '@capawesome/capacitor-badge'
import { Capacitor } from '@capacitor/core'

/**
 * Composable for managing iOS app icon badge count
 * Used to sync badge with unread actionable items (system messages)
 */
export function useAppBadge() {
  const isNative = Capacitor.isNativePlatform()
  const isIOS = Capacitor.getPlatform() === 'ios'

  /**
   * Set the badge count on the app icon
   * @param {number} count - Badge count (0 clears the badge)
   */
  const setBadge = async (count) => {
    if (!isNative) return

    try {
      if (count <= 0) {
        await Badge.clear()
        console.log('[Badge] Cleared')
      } else {
        await Badge.set({ count })
        console.log('[Badge] Set to:', count)
      }
    } catch (error) {
      console.error('[Badge] Error setting badge:', error)
    }
  }

  /**
   * Clear the badge from the app icon
   */
  const clearBadge = async () => {
    if (!isNative) return

    try {
      await Badge.clear()
      console.log('[Badge] Cleared')
    } catch (error) {
      console.error('[Badge] Error clearing badge:', error)
    }
  }

  /**
   * Get the current badge count
   * @returns {Promise<number>} Current badge count
   */
  const getBadge = async () => {
    if (!isNative) return 0

    try {
      const result = await Badge.get()
      return result.count
    } catch (error) {
      console.error('[Badge] Error getting badge:', error)
      return 0
    }
  }

  /**
   * Check if badge is supported on this platform
   * @returns {Promise<boolean>}
   */
  const isSupported = async () => {
    if (!isNative) return false

    try {
      const result = await Badge.isSupported()
      return result.isSupported
    } catch (error) {
      return false
    }
  }

  return {
    isNative,
    isIOS,
    setBadge,
    clearBadge,
    getBadge,
    isSupported
  }
}
